/**
 * États d'erreur, de chargement et vide — interaction-specification.md.
 *
 * Ces trois états sont des composants à part entière et non des `if` disséminés :
 * l'audit UX de la vague 11 vérifie que chaque écran les traite, et un état oublié
 * est précisément le défaut le plus fréquent d'une interface.
 */
import { useEffect, type JSX, type ReactNode } from 'react'

import { useAnnonce } from '@/components/RegionVivante'
import { useT } from '@/i18n/language'

export function LoadingState({ label }: { label?: string }): JSX.Element {
  const { t } = useT()
  return (
    <div className="state state--loading" role="status" aria-live="polite">
      <span className="state__spinner" aria-hidden="true" />
      <span>{label ?? t('state.loading', 'Chargement…')}</span>
    </div>
  )
}

/**
 * Pourquoi cet état vide ne propose AUCUNE action. Énuméré, jamais du texte libre : chacune de
 * ces trois valeurs est contestable en revue, alors qu'un « pas applicable » ne l'est pas.
 */
export type RaisonSansAction =
  /** Le rôle de l'utilisateur ne lui permet aucune action ici. */
  | 'lecture-seule'
  /** La donnée naît ailleurs, et `hint` dit où — sinon utiliser une action. */
  | 'amont'
  /** Liste produite par le système (journal, audit) : il n'y a rien à créer à la main. */
  | 'systeme'

/**
 * État vide.
 *
 * L'union est délibérée : un état vide DOIT soit porter l'action qui le résout, soit déclarer
 * explicitement pourquoi il n'en a pas. Le prop `action` existait déjà et 29 sites sur 42 ne
 * s'en servaient pas — une consigne de revue ne suffisait manifestement pas. Ici, le
 * compilateur refuse le silence.
 */
export type EmptyStateProps = {
  title: string
  hint?: string
  /** Annonce le passage à l'état vide (filtre appliqué, dernier élément supprimé). */
  annoncer?: boolean
  /**
   * `h1` quand l'état vide EST la page — l'écran ne rend rien d'autre (aucun programme choisi,
   * aucun projet ouvert) : sans lui, la page n'avait aucun titre (AUD-CRAWL-P11, AUD-CRAWL-K10).
   * Par défaut un paragraphe : l'état vide le plus courant vit sous l'en-tête d'un écran, qui
   * porte déjà le `h1`.
   */
  titreNiveau?: 'h1' | 'p'
} & (
  | { action: ReactNode; sansAction?: never }
  | { action?: never; sansAction: RaisonSansAction }
)

export function EmptyState({ title, hint, action, sansAction, annoncer, titreNiveau = 'p' }: EmptyStateProps): JSX.Element {
  const annonce = useAnnonce()
  useEffect(() => {
    if (annoncer) annonce(hint ? `${title}. ${hint}` : title)
  }, [annoncer, title, hint, annonce])
  const Titre = titreNiveau

  return (
    // `data-vide` rend la couverture mesurable de l'extérieur : un test peut vérifier qu'aucune
    // collection créable ne se déclare « systeme » pour s'exonérer d'une action.
    <div className="state state--empty" data-vide={sansAction ?? 'action'}>
      <Titre className="state__title">{title}</Titre>
      {/* Un état vide sans piste d'action laisse l'utilisateur bloqué. */}
      {hint ? <p className="state__hint">{hint}</p> : null}
      {action}
    </div>
  )
}

export function ErrorState({ message, detail, onRetry }: {
  message: string
  detail?: string
  onRetry?: () => void
}): JSX.Element {
  const { t } = useT()
  return (
    <div className="state state--error" role="alert">
      <p className="state__title">{message}</p>
      {detail ? <p className="state__hint">{detail}</p> : null}
      {/* Une erreur récupérable doit offrir la récupération, pas seulement la constater. */}
      {onRetry ? (
        <button type="button" className="button" onClick={onRetry}>
          {t('state.retry', 'Réessayer')}
        </button>
      ) : null}
    </div>
  )
}
